import ArchiveIcon from '@mui/icons-material/Archive';
import DeleteIcon from '@mui/icons-material/Delete';
import DraftsIcon from '@mui/icons-material/Drafts';
import MarkEmailUnreadIcon from '@mui/icons-material/MarkEmailUnread';
import {
  Box,
  CircularProgress,
  IconButton,
  Pagination,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Tooltip,
  Typography,
  useTheme,
} from '@mui/material';
import React, { Fragment, useEffect, useState } from 'react';
import { useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import AxiosConfig from '../../../config/AxiosConfig';
import EventTypeIcon from '../utils/common/EventTypeIcon.jsx';
import TypographyWithHighlightMentions from '../utils/common/TypographyWithHighlightMentions.jsx';

const groupNotificationsByDate = (notifications) => {
  const today = new Date();
  const yesterday = new Date();
  yesterday.setDate(today.getDate() - 1);

  return notifications.reduce((acc, notification) => {
    const notifDate = new Date(notification.createdAt);
    const notifDateString = notifDate.toDateString();

    let category;
    if (notifDateString === today.toDateString()) {
      category = "Aujourd'hui";
    } else if (notifDateString === yesterday.toDateString()) {
      category = 'Hier';
    } else if (
      notifDate >
      new Date(today.getFullYear(), today.getMonth(), today.getDate() - 7)
    ) {
      category = '7 derniers jours';
    } else if (notifDate.getMonth() === today.getMonth()) {
      category = 'Plus tôt ce mois-ci';
    } else {
      category = 'Autres';
    }

    if (!acc[category]) {
      acc[category] = [];
    }
    acc[category].push(notification);
    return acc;
  }, {});
};

const formatDate = (date) => {
  if (!date) {
    return '';
  }
  const notificationDate = new Date(date);
  const today = new Date();
  const isToday = notificationDate.toDateString() === today.toDateString();

  if (isToday) {
    return notificationDate.toLocaleTimeString([], {
      hour: '2-digit',
      minute: '2-digit',
    });
  } else {
    return notificationDate.toLocaleString('fr-FR', {
      month: 'long',
      day: 'numeric',
    });
  }
};

const Notifications = () => {
  const navigate = useNavigate();
  const notificationCount = useSelector(
    (state) => state?.login?.notificationCount
  );
  const [notifications, setNotifications] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [page, setPage] = useState(1);
  const [limit, setLimit] = useState(10);
  const [totalPages, setTotalPages] = useState(0);
  const theme = useTheme();

  const fetchNotifications = async () => {
    try {
      setLoading(true);
      await AxiosConfig.create('post', '/notifications/all')
        .call({ page, limit }) // Passer page et limit
        .exit((res) => {
          if (res?.data) {
            setNotifications(res.data.notifications);
            setTotalPages(res.data.totalPages);
          }
        });
    } catch (err) {
      setError(
        'Une erreur est survenue lors de la récupération des notifications.'
      );
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchNotifications();
  }, [notificationCount, page, limit]); // Rechercher les notifications lorsque la page ou la limite change

  const handleNavigate = async (notification, event) => {
    handleRead(notification, event);
    navigate(
      `/project/${notification?.ticket?.project?._id}/tickets/${
        notification?.ticket?._id
      }${notification?.task?._id ? `/tasks/${notification?.task?._id}` : ``}`
    );
  };

  const handleRead = async (notification, event) => {
    event.stopPropagation();
    try {
      await AxiosConfig.create(
        'put',
        `/notifications/${notification._id}/read`
      ).call();
      setNotifications((prev) =>
        prev.map((notif) =>
          notif._id === notification._id
            ? { ...notif, readAt: new Date() }
            : notif
        )
      );
    } catch (error) {
      console.error('Erreur lors de la lecture de la notification :', error);
    }
  };

  const handleUnread = async (notification, event) => {
    event.stopPropagation();
    try {
      await AxiosConfig.create(
        'put',
        `/notifications/${notification._id}/unread`
      ).call();
      setNotifications((prev) =>
        prev.map((notif) =>
          notif._id === notification._id ? { ...notif, readAt: null } : notif
        )
      );
    } catch (error) {
      console.error('Erreur lors de la lecture de la notification :', error);
    }
  };

  const handleArchive = async (notification, event) => {
    event.stopPropagation();
    try {
      await AxiosConfig.create(
        'put',
        `/notifications/${notification._id}/archive`
      ).call();
      setNotifications((prev) =>
        prev.map((notif) =>
          notif._id === notification._id
            ? { ...notif, isArchived: true }
            : notif
        )
      );
    } catch (error) {
      console.error("Erreur lors de l'archivage de la notification :", error);
    }
  };

  // Fonction de changement de page
  const handlePageChange = (event, value) => {
    setPage(value);
  };

  // Fonction de changement de limite
  const _handleLimitChange = (event) => {
    setLimit(event.target.value);
  };

  if (loading) {
    return (
      <Box
        display="flex"
        justifyContent="center"
        alignItems="center"
        height="100%"
      >
        <CircularProgress />
      </Box>
    );
  }

  if (error) {
    return <Typography color="error">{error}</Typography>;
  }

  return (
    <Box>
      <TableContainer component={Paper}>
        <Table stickyHeader>
          <TableHead>
            <TableRow>
              <TableCell
                colSpan={6}
                sx={{
                  textAlign: 'center',
                  fontSize: '0.875rem',
                  color: theme.palette.text.secondary,
                  backgroundColor: theme.palette.background.paper,
                  padding: '4px 0',
                }}
              >
                Les données ne sont pas en temps réel. Veuillez rafraîchir la
                page pour les mettre à jour.
              </TableCell>
            </TableRow>
            <TableRow>
              <TableCell>Ticket</TableCell>
              <TableCell>Emetteur</TableCell>
              <TableCell>Type</TableCell>
              <TableCell>Message</TableCell>
              <TableCell>Date</TableCell>
              <TableCell align="right">Actions</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {Object.entries(groupNotificationsByDate(notifications)).map(
              ([category, notifs]) => (
                <Fragment key={category}>
                  <TableRow>
                    <TableCell
                      colSpan={6}
                      sx={{
                        backgroundColor: theme.palette.background.default,
                        fontWeight: 'bold',
                      }}
                    >
                      {category}
                    </TableCell>
                  </TableRow>
                  {notifs.map((notification) => (
                    <TableRow
                      key={notification._id}
                      onClick={(event) => handleNavigate(notification, event)}
                      sx={{
                        cursor: 'pointer',
                        backgroundColor: notification.readAt
                          ? theme.palette.background.paper
                          : theme.palette.action.hover,
                      }}
                    >
                      <TableCell>
                        {notification?.ticket?.reference || 'N/A'}
                      </TableCell>
                      <TableCell>
                        <Typography>
                          <Tooltip
                            title={
                              notification?.emitter
                                ? `${notification?.emitter?.firstName} ${notification?.emitter?.lastName}`
                                : ''
                            }
                            arrow
                            placement="right"
                          >
                            <span>
                              {notification?.emitter
                                ? `${notification?.emitter?.firstName
                                    ?.charAt(0)
                                    .toUpperCase()}${notification?.emitter?.lastName
                                    ?.charAt(0)
                                    .toUpperCase()}`
                                : 'N/A'}
                            </span>
                          </Tooltip>
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <EventTypeIcon eventType={notification.eventType} />
                      </TableCell>
                      <TableCell>
                        {notification.eventType.code === 'NOTE_MENTION' && (
                          <TypographyWithHighlightMentions
                            text={notification?.note?.content}
                          />
                        )}
                        {notification.eventType.code === 'SCHEDULED_TASK' && (
                          <Typography>
                            {`${notification?.eventType?.name} : ${
                              notification?.task?.action?.name
                            }${
                              notification?.eventType?.code === 'SCHEDULED_TASK'
                                ? ` à ${formatDate(
                                    notification?.task?.scheduledAt
                                  )}`
                                : ''
                            }`}
                          </Typography>
                        )}
                        {[
                          'REFUND_ACCEPTED',
                          'REFUND_BLOCKED',
                          'REFUND_REFUSED',
                          'REFUND_FAILED',
                          'REFUND_DONE',
                          'REFUND_PENDING',
                        ].includes(notification?.eventType?.code) && (
                          <Typography>{`${
                            notification?.refund?.status?.name
                          } pour la commande ${
                            notification?.refund?.order?.reference
                          }${
                            notification?.refund?.refusedDescription &&
                            ['WAITINGFI', 'REFUSEDFI'].includes(
                              notification?.refund?.status?.code
                            )
                              ? ` : ${notification?.refund?.refusedDescription}`
                              : ``
                          }`}</Typography>
                        )}
                      </TableCell>
                      <TableCell>
                        {formatDate(notification.createdAt)}
                      </TableCell>
                      <TableCell align="right">
                        {notification.readAt ? (
                          <Tooltip
                            title={`Lue le : ${formatDate(
                              notification.readAt
                            )}`}
                          >
                            <IconButton
                              onClick={(event) =>
                                handleUnread(notification, event)
                              }
                            >
                              <DraftsIcon />
                            </IconButton>
                          </Tooltip>
                        ) : (
                          <IconButton
                            onClick={(event) => handleRead(notification, event)}
                          >
                            <MarkEmailUnreadIcon />
                          </IconButton>
                        )}

                        {notification.isArchived ? (
                          <Tooltip title={'Archivée'}>
                            <IconButton
                              onClick={(event) => event.stopPropagation()}
                            >
                              <ArchiveIcon />
                            </IconButton>
                          </Tooltip>
                        ) : (
                          <Tooltip title={'Archiver'}>
                            <IconButton
                              onClick={(event) =>
                                handleArchive(notification, event)
                              }
                            >
                              <DeleteIcon />
                            </IconButton>
                          </Tooltip>
                        )}
                      </TableCell>
                    </TableRow>
                  ))}
                </Fragment>
              )
            )}
          </TableBody>
        </Table>
      </TableContainer>

      {/* Pagination */}
      <Box display="flex" justifyContent="center" mt={2}>
        <Pagination
          count={totalPages}
          page={page}
          onChange={handlePageChange}
          color="primary"
        />
      </Box>
    </Box>
  );
};

export default Notifications;
